'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { publishPlatDisplayName } from '@/lib/market/publish-plat-display-name'
import { usePathname, useRouter } from 'next/navigation'
import {
  navigateQuery,
  readUrlSearchParams,
  useUrlSearchParams,
} from '@/lib/search/url-search-params.client'
import { mergeUrlSearchFilters } from '@/components/search/merge-url-search-filters'
import dynamic from 'next/dynamic'
import type { ListingTileRow, MapBounds } from '@/app/actions/listings'
import { countSearchListings, getViewportSearch, type SearchFilters } from '@/app/actions/search'
import type { SearchFiltersInitial } from '@/components/search/SearchFilters'
import type { ListingForMap } from '@/components/SearchMapClustered'
import {
  buildShapeSetForSearch,
  encodeMapPolygon,
  encodeMapShapes,
  type DrawnShape,
  type MapPolygonPoint,
} from '@/lib/map-polygon'
import { buildMapDrawPayload, buildZeroResultsPayload } from '@/lib/search/search-events'
import { fireSearchEvent } from '@/components/search/search-events.client'
import { ALL_SEARCH_URL_PARAMS, SEARCH_FIELDS } from '@/lib/search/field-registry'
import { publishSearchCountPair } from '@/lib/search/publish-search-count'
import { GEO_SCOPE_KEYS, geoScopeLabel, stripGeoScope } from '@/components/search/geo-scope'
import { nextSearchUrlWithBbox } from '@/lib/search/publish-map-bbox'
import { listingTileHref } from '@/lib/slug'
import { publishStreetLine } from '@/lib/listing/publish-street-line'
import { listingRowPhotoSrc } from '@/lib/listing/row-photo'
import { getHiddenListingKeys } from '@/app/actions/hidden-listings'
import { useViewerListingState } from '@/components/search/use-viewer-listing-state'
import { buildHiddenKeySet, excludeHiddenListings } from '@/components/search/hidden-exclusion'
import { withDeviceHiddenKeys } from '@/components/search/hidden-device'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import AreaPicker from '@/components/search/AreaPicker'
import { V3SourceDisclosure, type V3ListingRowBadge as ListingBadge } from '@/components/site/v3'
import { frameCountPending, rowsHoldWholeFrame, settledFrameBand } from '@/lib/search/settled-frame'
import { BEND_INTRO_CITY, publishBendDetachedIntro } from '@/lib/search/bend-detached-intro'
import { formatCount } from '@/lib/format/count'
import { searchListingCount, searchTypeFiltered } from '@/lib/listing-counts'
import { formatPriceCompact } from '@/lib/format/money'
import { SplitListingCard } from '@/components/search/SplitListingCard'
import { publishListingCardBadges } from '@/lib/listing/publish-listing-card-badges'
import { publishWholePropertyAmount } from '@/lib/listing/publish-listing-figure'
import { publishListingSharePricePerSqft } from '@/lib/listing/publish-listing-share'
import { publishTourEmbedFromUrl } from '@/lib/listing/publish-listing-hero-video'
import { ListingTourOverlay } from '@/components/site/listing-detail/ListingTourOverlay'
import type { VideoEmbed } from '@/lib/data/types/video'
import ListingCardHideControl from '@/components/listing/ListingCardHideControl'
import { SearchEmpty } from '@/app/search/_v3/SearchEmpty'
import { SearchPagerMore } from '@/app/search/_v3/SearchPager'
import { SPLIT_CARD_PAGE } from '@/lib/search/search-opening'
import { publishSplitClaim } from '@/lib/search/publish-split-claim'
import { isSoldSearchScope, searchSortLabel } from '@/lib/search/search-sort-order'
import './search-ledger.css'

/**
 * The map's stand-in, until the Maps script loads or (phones opening on the
 * list) until the reader asks for the map. One element for both, so a deferred
 * map serves the exact markup the dynamic import's loading state does and the
 * page hydrates without a mismatch.
 */
function MapLoading() {
  return (
    <div className="srch-map-field flex h-full w-full items-center justify-center text-muted-foreground" style={{ minHeight: 320 }}>
      Loading map…
    </div>
  )
}

const SearchMapClustered = dynamic(() => import('@/components/SearchMapClustered'), {
  ssr: false,
  loading: MapLoading,
})

/** The lg breakpoint (64rem): both panes side by side at and above it. */
const SPLIT_DESKTOP_QUERY = '(min-width: 64rem)'

/** Event-time read only (never during render), so SSR and hydration agree. */
function isSplitDesktop(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(SPLIT_DESKTOP_QUERY).matches
    : false
}

/** getViewportSearch's display cap (app/actions/search.ts `cap`). */
const VIEWPORT_ROW_CAP = 500

/** Street line only (e.g. "2732 NW Ordway Ave") — matches ListingCard addressLine. */
function cardStreet(l: ListingTileRow): string {
  return (
    publishStreetLine({
      streetNumber: l.StreetNumber,
      streetName: l.StreetName,
      streetSuffix: l.StreetSuffix,
    }) || (l.City ?? 'Listing')
  )
}

/** "Bend, OR 97703 · Awbrey Butte" — city + state + zip, plus subdivision when
 *  the row carries one. Oregon-scoped, so "OR" is a safe constant when State is
 *  absent. Mirrors SearchResults cityLine so split + list views read identically. */
function cardCity(l: ListingTileRow): string {
  const cityZip = [l.City ? `${l.City}, ${l.State ?? 'OR'}` : null, l.PostalCode].filter(Boolean).join(' ').trim()
  const sub = publishPlatDisplayName(l.SubdivisionName)
  return sub ? `${cityZip} · ${sub}` : cityZip
}

/** Whole-dollar list price per living sqft, or null when either input is missing. */
function cardPricePerSqft(l: ListingTileRow): number | null {
  const sqft = rowSqft(l)
  if (l.ListPrice == null || sqft == null || sqft <= 0) return null
  return Math.round(l.ListPrice / sqft)
}

/**
 * Sort options for the list-pane count row (G2), labeled from the one sort
 * table (lib/search/search-sort-order.ts) for the scope in view: `newest`
 * reads "Newest listed" because it orders by the on-market date, and
 * "Recently sold" on the Sold scope, where it orders by the close date
 * (Matt 2026-09-23).
 */
const SORT_KEYS = [
  'newest',
  'price_asc',
  'price_desc',
  'oldest',
  'price_per_sqft_asc',
  'price_per_sqft_desc',
  'year_newest',
  'year_oldest',
] as const

function sortOptionsFor(sold: boolean) {
  return SORT_KEYS.map((value) => ({ value, label: searchSortLabel(value, { sold }) }))
}

/** Compact filter crumbs for the mockup count row ("N homes · Bend · $500K+"). */
function buildFiltersSummary(f: SearchFiltersInitial): string {
  const parts: string[] = []
  if (f.schoolDistrict?.trim()) parts.push(f.schoolDistrict.trim())
  if (f.city?.trim()) parts.push(f.city.trim())
  if (f.subdivision?.trim()) parts.push(f.subdivision.trim())
  if (f.postalCode?.trim()) parts.push(f.postalCode.trim())
  const minP = f.minPrice?.trim() ? Number(f.minPrice) : null
  const maxP = f.maxPrice?.trim() ? Number(f.maxPrice) : null
  const fmt = (n: number) =>
    n >= 1_000_000
      ? `$${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`
      : n >= 1_000
        ? `$${Math.round(n / 1_000)}K`
        : `$${n}`
  if (minP != null && Number.isFinite(minP) && maxP != null && Number.isFinite(maxP)) {
    parts.push(`${fmt(minP)} to ${fmt(maxP)}`)
  } else if (minP != null && Number.isFinite(minP)) {
    parts.push(`${fmt(minP)}+`)
  } else if (maxP != null && Number.isFinite(maxP)) {
    parts.push(`Up to ${fmt(maxP)}`)
  }
  if (f.beds?.trim()) parts.push(`${f.beds}+ bd`)
  if (f.baths?.trim()) parts.push(`${f.baths}+ ba`)
  if (f.propertyType?.trim()) parts.push(f.propertyType.trim())
  if (f.status && f.status !== 'Active') parts.push(f.status)
  return parts.join(' · ')
}

/** Optional 4th arg for pan payload cap. Server may ignore until Wave 3 API lands. */
type ViewportSearchOptions = { limit?: number }
type ViewportSearchFn = (
  filters: SearchFilters,
  bounds: MapBounds,
  polygon: Parameters<typeof getViewportSearch>[2],
  options?: ViewportSearchOptions
) => ReturnType<typeof getViewportSearch>

/**
 * Map viewport badge → ListingCard badge prop.
 * Non-active MLS status uses publishListingStatusBadge (Pending / Under
 * contract / Sold). `nowMs` is a server-passed wall-clock prop so SSR HTML
 * matches hydration (do not sample the clock inside this pure helper).
 */
function cardBadges(
  l: ListingTileRow,
  nowMs: number,
  openHouseLabels: Record<string, string>,
): { kind: ListingBadge; label: string }[] {
  const key = String(l.ListingKey ?? '').trim()
  return publishListingCardBadges({
    nowMs,
    standardStatus: l.StandardStatus,
    onMarketDate: l.OnMarketDate,
    priceDropCount: l.price_drop_count ?? null,
    priceDropAmount: l.price_drop_amount ?? null,
    lastPriceChangeTimestamp: l.last_price_change_timestamp ?? null,
    listPrice: l.ListPrice ?? null,
    originalListPrice: l.originalListPrice ?? null,
    hasVirtualTour: l.has_virtual_tour === true || Boolean(l.tourUrl),
    hasTourUrl: Boolean(l.tourUrl),
    openHouseLabel: key ? openHouseLabels[key] ?? null : null,
  })
}

function rowKey(l: ListingTileRow): string {
  return String(l.ListNumber ?? l.ListingKey ?? '').trim()
}

/** ListingTileRow carries TotalLivingAreaSqFt at runtime from the viewport fetch even though the base type omits it. */
function rowSqft(l: ListingTileRow): number | null {
  const v = (l as { TotalLivingAreaSqFt?: number | null }).TotalLivingAreaSqFt
  return v ?? null
}

function toMapListing(l: ListingTileRow): ListingForMap {
  return {
    Latitude: l.Latitude,
    Longitude: l.Longitude,
    ListingKey: l.ListingKey,
    ListNumber: l.ListNumber ?? null,
    ListPrice: l.ListPrice,
    StreetNumber: l.StreetNumber,
    StreetName: l.StreetName,
    StreetSuffix: l.StreetSuffix ?? null,
    City: l.City,
    State: l.State,
    PostalCode: l.PostalCode,
    BedroomsTotal: l.BedroomsTotal,
    BathroomsTotal: l.BathroomsTotal,
    PhotoURL: l.PhotoURL ? listingRowPhotoSrc(l.PhotoURL) : l.PhotoURL,
    TotalLivingAreaSqFt: rowSqft(l),
    PropertyType: l.PropertyType,
    PropertySubType: l.PropertySubType,
  }
}

/** Convert the page's URL filter object into the server-action SearchFilters shape. */
function toSearchFilters(f: SearchFiltersInitial): SearchFilters {
  // Registry fields ride along as raw URL-param strings (the server page
  // spreads every ALL_SEARCH_URL_PARAMS hit into the filters prop). Coerce
  // them per the field registry so a pan/zoom refetch keeps every active
  // filter — booleans `1` -> true, multi CSV -> string[], ranges -> numbers.
  const raw = f as Record<string, string | undefined>
  const registry: Record<string, unknown> = {}
  for (const def of SEARCH_FIELDS) {
    if (def.kind === 'boolean') {
      if (raw[def.key] === '1') registry[def.key] = true
    } else if (def.kind === 'multi') {
      const value = raw[def.key]
      if (value?.trim()) {
        const values = value.split(',').map((v) => v.trim()).filter(Boolean)
        if (values.length > 0) registry[def.key] = values
      }
    } else if (def.kind === 'text') {
      if (def.key === 'keywords') continue // handled below with the base fields
      const value = raw[def.key]?.trim()
      if (value) registry[def.key] = value
    } else {
      if (def.key === 'dom') continue // rides the legacy daysOnMarket string below
      const params = def.legacyParams
        ? [def.legacyParams.min, def.legacyParams.max]
        : [`${def.key}Min`, `${def.key}Max`]
      for (const param of params) {
        if (!param) continue
        const value = raw[param]
        if (value == null || value.trim() === '') continue
        const parsed = Number(value)
        if (Number.isFinite(parsed)) registry[param] = parsed
      }
    }
  }
  return {
    ...(registry as Partial<SearchFilters>),
    city: f.city || undefined,
    subdivision: f.subdivision || undefined,
    neighborhood: f.neighborhood || undefined,
    minPrice: f.minPrice ? Number(f.minPrice) : undefined,
    maxPrice: f.maxPrice ? Number(f.maxPrice) : undefined,
    beds: f.beds ? Number(f.beds) : undefined,
    baths: f.baths ? Number(f.baths) : undefined,
    maxBeds: f.maxBeds ? Number(f.maxBeds) : undefined,
    maxBaths: f.maxBaths ? Number(f.maxBaths) : undefined,
    // Empty string is active+pending (SEO map / getViewportSearch). Do not
    // coerce '' to Active — only default when status is actually missing.
    status: f.status ?? 'Active',
    sort: f.sort || 'newest',
    minSqFt: f.minSqFt ? Number(f.minSqFt) : undefined,
    maxSqFt: f.maxSqFt ? Number(f.maxSqFt) : undefined,
    lotAcresMin: f.lotAcresMin != null && f.lotAcresMin !== '' ? Number(f.lotAcresMin) : undefined,
    lotAcresMax: f.lotAcresMax != null && f.lotAcresMax !== '' ? Number(f.lotAcresMax) : undefined,
    yearBuiltMin: f.yearBuiltMin ? Number(f.yearBuiltMin) : undefined,
    yearBuiltMax: f.yearBuiltMax ? Number(f.yearBuiltMax) : undefined,
    propertyType: f.propertyType && f.propertyType !== 'all' ? f.propertyType : undefined,
    postalCode: f.postalCode || undefined,
    garageMin: f.garageMin ? Number(f.garageMin) : undefined,
    daysOnMarket: f.daysOnMarket || undefined,
    keywords: f.keywords || undefined,
    hasPool: f.hasPool === '1' ? true : undefined,
    hasView: f.hasView === '1' ? true : undefined,
    hasWaterfront: f.hasWaterfront === '1' ? true : undefined,
    hasFireplace: f.hasFireplace === '1' ? true : undefined,
    hasGolfCourse: f.hasGolfCourse === '1' ? true : undefined,
  }
}

export type MapSearchViewProps = {
  initialListings: ListingTileRow[]
  initialTotalCount: number
  initialCapped: boolean
  /**
   * False when initialTotalCount is only a floor (the rows fetched), because
   * the uncapped count did not come back in time: the claim prints "N+".
   * Unset means the count is the real one.
   */
  initialCountIsExact?: boolean
  /** Detached houses in the seed's frame (getViewportSearch detachedCount); the Bend intro line only. */
  initialDetachedCount?: number | null
  initialBounds: MapBounds
  filters: SearchFiltersInitial
  /** SSR seed only — the hydrated value comes from useViewerListingState so a cacheable (visitor-independent) shell still shows the right saves after mount. */
  savedListingKeys: string[]
  likedListingKeys: string[]
  placeQuery: string
  boundaryGeojson?: unknown
  /** Subordinate boundary cells (a community's plats), drawn lighter than the seed ring; href makes a cell a door. */
  overlayBoundaries?: Array<{ label: string; href?: string; geojson: unknown }>
  initialPolygon?: MapPolygonPoint[] | null
  /** Multi-shape draw set from ?shapes= (falls back to ?poly= via initialPolygon). */
  initialShapes?: DrawnShape[] | null
  /** Server-computed request timestamp (ms) for the card "New" badge — see
   *  cardBadge()'s comment for why this isn't read client-side. */
  nowMs?: number
  /**
   * SSR viewport timed out or failed (SEARCH_UX_WAVE3 P9). When true the empty
   * state must NOT claim "no homes" — show a retry instead of inventing zero inventory.
   */
  initialDegraded?: boolean
  /** Place pages: the city pin is the page. Do not offer "clear Redmond". */
  lockPlace?: boolean
  /** listingKey → upcoming open-house badge label. */
  openHouseLabels?: Record<string, string>
  /**
   * The living atlas on the page is the map. Do not mount Google. List of
   * homes only — zoom, listing clicks, and place jumps live on V3Atlas.
   */
  listOnly?: boolean
  /**
   * The page is a static shell (SITE-29): `filters` and the initial listings
   * are the prerendered DEFAULTS, the URL's query is laid over them after
   * mount, a query change refetches the viewport here (the route's RSC
   * payload is static, so no server re-render carries new props), and URL
   * writes go to history.pushState instead of router.push.
   */
  staticShell?: boolean
  /**
   * The place's own footprint (seed ring or plat union), the scope the server
   * searched inside for the prerendered list. A static-shell refetch searches
   * inside the same footprint, so a filtered list and its count describe the
   * same population the page opened on. Without it the refetch would fall back
   * to the bounding box plus the place's name tag, a different population
   * (measured 2026-09-09 on /cities/bend/awbrey-butte: 70 in the ring, more by
   * tag), and the count row would print the wrong number or none.
   */
  scopePolygon?: Parameters<typeof getViewportSearch>[2]
  /**
   * The pane a phone opens on (below lg; lib/search/search-opening.ts
   * phoneOpeningPane). 'list' fills the frame with the list, puts the map one
   * tap away behind a floating Map | Sort pill, and does not mount the map
   * until that tap. The markup is the same at every width; CSS lays it out.
   * Unset keeps the map-first sheet.
   */
  phonePane?: 'list' | 'map'
  /**
   * The regional frame (bare /homes-for-sale): the place the frame names,
   * 'Central Oregon'. Its population is the service-area set, not the pixel
   * viewport (getViewportSearch frame 'region'), its first settle writes no
   * camera URL, and the server seeded only the first card page, so the pins
   * are read here once the map mounts. Null for every map frame.
   */
  regionFrameLabel?: string | null
  /**
   * The row limit the server seeded with, when it was below the viewport cap
   * (the regional frame seeds SPLIT_CARD_PAGE). Unset means the seed was read
   * at the full cap. A read's row count alone cannot say this: the DAL can
   * return a few rows under its limit, so "fewer rows than asked" is not "more
   * rows exist".
   */
  seedRowCap?: number
}

export default function MapSearchView({
  initialListings,
  initialTotalCount,
  initialCapped,
  initialCountIsExact = true,
  initialDetachedCount = null,
  initialBounds,
  filters: filtersProp,
  savedListingKeys: savedListingKeysSeed,
  likedListingKeys: likedListingKeysSeed,
  placeQuery,
  boundaryGeojson,
  overlayBoundaries,
  initialPolygon: initialPolygonProp = null,
  initialShapes = null,
  nowMs,
  initialDegraded = false,
  lockPlace = false,
  openHouseLabels = {},
  listOnly = false,
  staticShell = false,
  scopePolygon = null,
  phonePane,
  regionFrameLabel = null,
  seedRowCap,
}: MapSearchViewProps) {
  const listFirst = !listOnly && phonePane === 'list'
  const seedCap = Math.min(seedRowCap ?? VIEWPORT_ROW_CAP, VIEWPORT_ROW_CAP)
  // Static-safe query read (lib/search/url-search-params.client): '' at
  // hydration, the live URL after. On a static shell the URL wins over the
  // prerendered defaults; a place page keeps its geo pin (lockPlace).
  const urlSearchParams = useUrlSearchParams()
  const filters = useMemo(
    () => (staticShell ? mergeUrlSearchFilters(filtersProp, urlSearchParams, { lockPlace }) : filtersProp),
    [staticShell, filtersProp, urlSearchParams, lockPlace],
  )
  // One initial shape set, whichever URL spelling delivered it: ?shapes=
  // (multi-shape) wins; a legacy ?poly= ring arrives as a single include
  // polygon. `initialPolygon` keeps its historical meaning for the scope-drop
  // + re-seed logic: non-null exactly when the URL carried ANY include shape.
  const initialDrawn = useMemo<DrawnShape[]>(() => {
    if (initialShapes && initialShapes.length > 0) return initialShapes
    if (initialPolygonProp && initialPolygonProp.length >= 3) {
      return [{ type: 'polygon', points: initialPolygonProp, exclude: false }]
    }
    return []
  }, [initialShapes, initialPolygonProp])
  const initialPolygon = initialDrawn.some((s) => !s.exclude) ? initialDrawn : null
  const [listings, setListings] = useState(initialListings)
  const [totalCount, setTotalCount] = useState(initialTotalCount)
  const [capped, setCapped] = useState(initialCapped)
  const [countIsExact, setCountIsExact] = useState(initialCountIsExact)
  const [detachedCount, setDetachedCount] = useState<number | null>(initialDetachedCount)
  // Timeout honesty (P9): seed from SSR; clear on a successful viewport fetch.
  const [resultsDegraded, setResultsDegraded] = useState(initialDegraded)
  // Local sort so the Select stays in sync while URL replace + viewport refetch run.
  const [sortValue, setSortValue] = useState(filters.sort?.trim() || 'newest')
  // Which floating pill's sort menu is open: the map's, or the list-first
  // phone's Map | Sort door. One at a time.
  const [sortOpen, setSortOpen] = useState<'map' | 'door' | null>(null)
  const [loading, setLoading] = useState(false)
  // Per-user hidden homes ("Hide homes I don't want to see"). Same edge-of-
  // render model as SearchResults: viewport results are SHARED caches, so the
  // per-user subtraction never touches the fetch — a hidden home is filtered
  // out of BOTH the card list AND the map pins here, from the signed-in user's
  // hidden_listings rows. Signed-out users get an empty set (no filtering).
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(() => new Set())
  const [areaDirty, setAreaDirty] = useState(false)
  const [hoveredKey, setHoveredKey] = useState<string | null>(null)
  /** Pin/list selection (stronger than hover) — map popup open ↔ list card ring + scroll. */
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [tour, setTour] = useState<VideoEmbed | null>(null)
  const [drawnShapes, setDrawnShapes] = useState<DrawnShape[]>(initialDrawn)
  const [layoutView, setLayoutView] = useState<'list' | 'map' | 'split'>(
    listOnly
      ? 'list'
      : filters.view === 'list' || filters.view === 'map' || filters.view === 'split'
        ? filters.view
        : 'split',
  )
  // Phones opening on the list (phonePane 'list') start on the list pane even
  // though the server rendered split; everything else keeps the map first.
  const [mobileView, setMobileView] = useState<'list' | 'map'>(
    listOnly
      ? 'list'
      : listFirst
        ? 'list'
        : filters.view === 'map' || filters.view === 'split' ? 'map' : 'list',
  )
  // The Google map mounts only when it can be seen. A phone opening on the
  // list gets the placeholder (the same markup the server rendered) until the
  // reader taps Map; from lg up it mounts right after hydration.
  const [mapWanted, setMapWanted] = useState(!listFirst)
  // Window the CARD list so the SSR payload + hydration cost stays small even
  // when the viewport returns hundreds of homes. The MAP still gets every pin
  // (mapListings below) — only the heavy cards are paged in. "Show more" reveals
  // the rest from already-loaded data, and reads the rest of the frame when the
  // server seeded only its first page (the regional frame).
  const CARD_PAGE = SPLIT_CARD_PAGE
  const [visibleCount, setVisibleCount] = useState(CARD_PAGE)
  // The regional frame (regionFrameLabel): its population is the region, not
  // the viewport, until the reader moves the map or draws an area. The ref
  // steers the next read; `rowsAreRegion` says what the rows IN HAND describe,
  // so the claim names the frame they were read for, not one still loading.
  const regionFrameRef = useRef(Boolean(regionFrameLabel))
  const [rowsAreRegion, setRowsAreRegion] = useState(Boolean(regionFrameLabel))
  const setRegion = useCallback((on: boolean) => {
    regionFrameRef.current = on
  }, [])
  // One number per server seed: the pin read below runs once per seed.
  const [seedGen, setSeedGen] = useState(0)
  // A read has landed since the current server seed: the rows in hand are no
  // longer the seed's first page. Until then a partial seed's total is not
  // printed (lib/search/settled-frame.ts), so it never shows before the
  // total the page settles on.
  const [seedReplaced, setSeedReplaced] = useState(false)
  const pinReadGenRef = useRef(-1)
  // The row limit the rows in hand were read with: the seed's cap, then the
  // limit of each client read (a pan's Search this area reads 250).
  const [rowsCap, setRowsCap] = useState(seedCap)

  const listContainerRef = useRef<HTMLDivElement>(null)
  const sheetDragRef = useRef<{ startY: number; expanded: boolean } | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastBoundsRef = useRef<MapBounds>(initialBounds)
  // The frame the list in hand was read for (lastBoundsRef is the camera, which
  // can move ahead of it until Search this area). "Show more" reads the rest
  // of THIS frame, never a camera the reader has not searched.
  const queryBoundsRef = useRef<MapBounds>(initialBounds)
  const reqIdRef = useRef(0)

  const viewerState = useViewerListingState({
    savedListingKeys: savedListingKeysSeed,
    likedListingKeys: likedListingKeysSeed,
  })
  const searchFilters = useMemo(() => toSearchFilters(filters), [filters])
  const filtersSnapshot = JSON.stringify(filters)
  useEffect(() => {
    setSortValue(filters.sort?.trim() || 'newest')
  }, [filters.sort])
  // Radix SelectValue only paints a label once SelectContent has mounted at
  // least once (a client-only useLayoutEffect) — with no children of its own
  // it renders truly empty in SSR HTML, which a crawler sees as a blank
  // control. Pass the label as SelectValue's children so the served markup
  // always carries the current sort's text, SSR and post-hydration alike.
  // The Sold scope names its date sorts by the close date they order by.
  const soldScope = isSoldSearchScope(filters.status)
  const sortOptions = useMemo(() => sortOptionsFor(soldScope), [soldScope])
  const sortLabel = searchSortLabel(sortValue, { sold: soldScope })

  // ── Geo scope (W4.2) ──────────────────────────────────────────────────────
  // The URL can pin the search to a place (city / subdivision / zip). That pin
  // is honest only while the map still shows that place — once the user MOVES
  // the map (or draws an area), the viewport query becomes pure bounding-box.
  // Keeping an invisible city=Bend filter while the map sits over Redmond
  // returned zero rows with a misleading empty state. Until the first move,
  // the scope is visible as a chip on the map canvas with clear-on-tap.
  const scopeLabel = useMemo(
    () =>
      geoScopeLabel({
        city: filters.city,
        subdivision: filters.subdivision,
        neighborhood: filters.neighborhood,
        schoolDistrict: filters.schoolDistrict,
        postalCode: filters.postalCode,
      }),
    [filters.city, filters.subdivision, filters.neighborhood, filters.schoolDistrict, filters.postalCode]
  )
  // A URL that arrives with ?poly= encodes a search whose place pin was
  // ALREADY superseded by the drawn shape (drawing calls dropGeoScope, and the
  // server strips the geo scope from the initial viewport fetch when poly is
  // present) — so the scope starts dropped and the chip stays hidden. That is
  // what makes reload/share reproduce the exact post-draw state.
  const [scopeDropped, setScopeDropped] = useState(initialPolygon != null)
  // Refs mirror the state so the debounced viewport fetch (350 ms setTimeout)
  // reads the CURRENT scope decision at fire time, not the closure it was
  // created with — a pan that drops the scope must not race its own refetch.
  const scopeDroppedRef = useRef(initialPolygon != null)
  const searchFiltersRef = useRef(searchFilters)
  useEffect(() => {
    searchFiltersRef.current = searchFilters
  }, [searchFilters])
  const scopePolygonRef = useRef(scopePolygon)
  useEffect(() => {
    scopePolygonRef.current = scopePolygon
  }, [scopePolygon])
  // The map's FIRST bounds report is its initial settle (fitBounds on load),
  // not a user gesture — it must not drop the scope. Every report after that
  // is a real pan/zoom/re-center. A short grace window covers the async
  // place-viewport fit (SearchMapClustered's PlacesService callback fires a
  // SECOND idle after the first settle when no boundary polygon is available).
  const firstBoundsReportRef = useRef(true)
  const INITIAL_SETTLE_GRACE_MS = 2500
  const initialSettleUntilRef = useRef(0)
  useEffect(() => {
    // Stamps the settle window when the map mounts: at component mount, or
    // later when a phone that opened on the list taps Map (the map's first fit
    // and its relayout are its settle, not a pan).
    if (!mapWanted) return
    firstBoundsReportRef.current = true
    initialSettleUntilRef.current = Date.now() + INITIAL_SETTLE_GRACE_MS
  }, [mapWanted])
  // From lg up both panes show, so the map mounts right after hydration. Below
  // lg a list-first phone mounts it on the Map tap (applyView), or here if the
  // window grows past lg.
  useEffect(() => {
    if (mapWanted || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mql = window.matchMedia(SPLIT_DESKTOP_QUERY)
    if (mql.matches) {
      setMapWanted(true)
      return
    }
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) setMapWanted(true)
    }
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [mapWanted])
  // Set after the effect above, so the pass that learns whether the map
  // mounts is the same pass that ends the server render's pending count.
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    setHydrated(true)
  }, [])
  const router = useRouter()
  const pathname = usePathname()
  const applyView = useCallback(
    (next: 'list' | 'map' | 'split') => {
      // A list-first phone flips panes in place: the list and the map are one
      // tap apart, the URL stays the indexable one, nothing re-renders on the
      // server. From lg up the toggle navigates, as it always has.
      if (listFirst && next !== 'split' && !isSplitDesktop()) {
        setMobileView(next)
        if (next === 'map') setMapWanted(true)
        return
      }
      setLayoutView(next)
      setMobileView(next === 'list' ? 'list' : 'map')
      // Event-time read — a closed-over searchParams can lag a filter pushState
      // and drop minPrice / beds when the view toggle writes (SITE-72).
      const params = new URLSearchParams(readUrlSearchParams())
      params.set('view', next)
      navigateQuery(router, `${pathname ?? '/homes-for-sale'}?${params.toString()}`, { staticShell })
    },
    [listFirst, pathname, router, staticShell],
  )
  useEffect(() => {
    if (filters.view === 'list' || filters.view === 'map' || filters.view === 'split') {
      setLayoutView(filters.view)
      // A list-first page renders split for lg and up; its phone pane stays
      // the list rather than snapping to the map after hydration.
      setMobileView(filters.view === 'list' || (listFirst && filters.view === 'split') ? 'list' : 'map')
    }
  }, [filters.view, listFirst])
  // Once-per-distinct-query guard for search_zero_results — a pan across the
  // same empty search must not re-fire; a CHANGED query that dead-ends must.
  const zeroResultsFiredKeyRef = useRef<string | null>(null)
  // Zero results can come from tight filters, not just the map viewport — the
  // empty state names the likely cause and offers the same reset as "Clear all".
  // Registry params (fireplace, shop, well water, …) count as narrowing too;
  // enumerating them from the registry keeps this in step as fields are added.
  const hasNarrowingFilters = useMemo(() => {
    const narrowing = [...ALL_SEARCH_URL_PARAMS, 'propertyType', 'propertySubType']
    return narrowing.some((k) => {
      if (k === 'view' || k === 'sort' || k === 'page') return false
      const v = (filters as Record<string, string | undefined>)[k]
      return typeof v === 'string' && v.trim() !== ''
    })
  }, [filters])

  // When the viewport shows zero but the same filters match homes elsewhere,
  // say so with the real number — a bare "0 homes" reads as "we have nothing"
  // when the matches are simply outside the current map (rural listings around
  // a city are the common case).
  const [beyondViewportCount, setBeyondViewportCount] = useState<number | null>(null)
  const [matchCount, setMatchCount] = useState<number | null>(null)
  const [matchCountReady, setMatchCountReady] = useState(false)
  const hasDrawnShapes = drawnShapes.length > 0
  useEffect(() => {
    if (totalCount !== 0 || !hasNarrowingFilters || hasDrawnShapes) {
      setBeyondViewportCount(null)
      return
    }
    let cancelled = false
    const params: Record<string, string> = {}
    for (const [k, v] of Object.entries(filters as Record<string, string | undefined>)) {
      // Once the user has moved the map the geo pin no longer applies to the
      // viewport query, so the "matches elsewhere" count must not apply it
      // either — the two numbers would describe different searches.
      if (scopeDropped && (GEO_SCOPE_KEYS as readonly string[]).includes(k)) continue
      if (typeof v === 'string' && v.trim() !== '') params[k] = v
    }
    countSearchListings(params)
      .then((n) => {
        if (!cancelled) setBeyondViewportCount(n != null && n > 0 ? n : null)
      })
      .catch(() => {
        if (!cancelled) setBeyondViewportCount(null)
      })
    return () => {
      cancelled = true
    }
  }, [totalCount, hasNarrowingFilters, hasDrawnShapes, scopeDropped, filtersSnapshot]) // eslint-disable-line react-hooks/exhaustive-deps

  // Filter-match count (no bbox) — the coherent number for the URL filters.
  // Viewport totalCount is a different population and may only print labeled.
  useEffect(() => {
    let cancelled = false
    setMatchCountReady(false)
    const params: Record<string, string> = {}
    for (const [k, v] of Object.entries(filters as Record<string, string | undefined>)) {
      if (scopeDropped && (GEO_SCOPE_KEYS as readonly string[]).includes(k)) continue
      if (typeof v === 'string' && v.trim() !== '') params[k] = v
    }
    countSearchListings(params)
      .then((n) => {
        if (!cancelled) {
          setMatchCount(n)
          setMatchCountReady(true)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMatchCount(null)
          setMatchCountReady(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [scopeDropped, filtersSnapshot]) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-seed from server props whenever the URL filters change (new SSR payload).
  // The SSR payload is scoped again, so the scope-drop resets with it — the
  // chip re-appears and the next user move re-drops it.
  // Static shell (SITE-29): the server props are the prerendered defaults and
  // never change after mount. A URL change is the refetch effect's job below;
  // re-seeding here would snap the list back to the defaults.
  const reseededRef = useRef(false)
  useEffect(() => {
    if (staticShell && reseededRef.current) return
    const firstSeed = !reseededRef.current
    reseededRef.current = true
    // A new server seed supersedes any read still in flight for the old one
    // (a stale pin read must not paint over a newer seed), so bump the request
    // id and clear the spinner that read owned.
    reqIdRef.current += 1
    setLoading(false)
    setListings(initialListings)
    setTotalCount(initialTotalCount)
    setCapped(initialCapped)
    setCountIsExact(initialCountIsExact)
    setDetachedCount(initialDetachedCount)
    setResultsDegraded(initialDegraded)
    setDrawnShapes(initialDrawn)
    setVisibleCount(CARD_PAGE)
    scopeDroppedRef.current = initialPolygon != null
    setScopeDropped(initialPolygon != null)
    setAreaDirty(false)
    setRegion(Boolean(regionFrameLabel))
    setRowsAreRegion(Boolean(regionFrameLabel))
    setRowsCap(seedCap)
    setSeedReplaced(false)
    queryBoundsRef.current = initialBounds
    // The mount seed is generation 0; each later server seed is a new one.
    if (!firstSeed) setSeedGen((g) => g + 1)
  }, [initialListings, initialTotalCount, initialCapped, initialCountIsExact, initialDetachedCount, initialDegraded, initialDrawn, filtersSnapshot, regionFrameLabel, seedCap]) // eslint-disable-line react-hooks/exhaustive-deps

  // Clean up any pending debounce on unmount.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const runViewportSearch = useCallback(
    async (bounds: MapBounds, shapes: DrawnShape[], opts?: ViewportSearchOptions) => {
      const reqId = ++reqIdRef.current
      setLoading(true)
      try {
        // Read filters + scope decision through refs at FIRE time: this call
        // often runs from a 350 ms debounce, and the pan that scheduled it may
        // have just dropped the geo scope. After the drop the query is pure
        // bounding-box — no invisible city/subdivision/zip pin.
        const base = searchFiltersRef.current
        const effectiveFilters = scopeDroppedRef.current ? stripGeoScope(base) : base
        // The drawn set becomes the server's include/exclude shapes contract
        // (PostGIS). Exclude-only sets ride the current viewport as the
        // include ring — "this view minus those areas". A place page with no
        // drawn shapes keeps the footprint its server list was scoped to.
        const poly =
          shapes.length === 0 && scopePolygonRef.current != null
            ? scopePolygonRef.current
            : buildShapeSetForSearch(shapes, bounds)
        queryBoundsRef.current = bounds
        // The regional frame reads the region (no bbox) until the reader moves
        // the map or draws; `bounds` is only its camera then.
        const regionFrame = regionFrameRef.current && shapes.length === 0 && poly == null
        // Pan passes { limit: 250 } for a lighter payload (P3). Non-pan paths
        // (draw, toggle, retry) keep the three-arg call so SSR-style fidelity
        // remains the default; the 4th arg is TypeScript-optional until the
        // server action accepts it.
        const res = regionFrame
          ? await getViewportSearch(effectiveFilters, bounds, null, {
              frame: 'region',
              ...(opts?.limit != null ? { limit: opts.limit } : {}),
            })
          : opts?.limit != null
            ? await (getViewportSearch as ViewportSearchFn)(effectiveFilters, bounds, poly, {
                limit: opts.limit,
              })
            : await getViewportSearch(effectiveFilters, bounds, poly)
        // Ignore out-of-order responses (user kept panning).
        if (reqId !== reqIdRef.current) return
        setListings(res.listings)
        setTotalCount(res.totalCount)
        setCapped(res.capped)
        setCountIsExact(res.countIsExact !== false)
        setDetachedCount(res.detachedCount ?? null)
        setResultsDegraded(false)
        setRowsAreRegion(regionFrame)
        setRowsCap(Math.min(opts?.limit ?? VIEWPORT_ROW_CAP, VIEWPORT_ROW_CAP))
        setSeedReplaced(true)
        // Instrumentation (Phase 0.5): a search round-trip that dead-ends at 0
        // homes, keyed by the live query string so repeated pans over the same
        // empty search fire once. Fire-and-forget — never touches the fetch.
        if (res.totalCount === 0 && typeof window !== 'undefined') {
          const payload = buildZeroResultsPayload(window.location.search)
          const key = JSON.stringify(payload)
          if (zeroResultsFiredKeyRef.current !== key) {
            zeroResultsFiredKeyRef.current = key
            fireSearchEvent('search_zero_results', payload)
          }
        }
      } catch {
        // Network / action failure: honest degraded empty, not "0 homes".
        if (reqId === reqIdRef.current) {
          setResultsDegraded(true)
          setSeedReplaced(true)
        }
      } finally {
        if (reqId === reqIdRef.current) setLoading(false)
      }
    },
    []
  )

  // Static shell (SITE-29): the URL is the filter state and the route's RSC
  // payload is static, so a query change refetches the viewport here. The ref
  // holds the query the CURRENT list was fetched with, seeded from the server
  // props (the prerendered defaults), never from the merged filters: on a
  // desktop place page the atlas remounts this component after its first
  // camera fit, by which time the store already carries the URL, and a ref
  // seeded from the merged snapshot would see no change and never refetch a
  // deep-linked ?beds=4 (measured 2026-09-09: phones refetched, desktop did
  // not). Seeded from the defaults, a mount under a filtered URL is a change.
  const fetchedSnapshotRef = useRef(staticShell ? JSON.stringify(filtersProp) : filtersSnapshot)
  useEffect(() => {
    if (!staticShell) return
    if (filtersSnapshot === fetchedSnapshotRef.current) return
    fetchedSnapshotRef.current = filtersSnapshot
    setVisibleCount(CARD_PAGE)
    void runViewportSearch(lastBoundsRef.current, drawnShapes)
    // drawnShapes is read for the call, not a trigger: a shape change runs
    // its own search in handleShapesChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staticShell, filtersSnapshot, runViewportSearch])

  /** Mockup G2: sort lives on the count row; updates URL like other filters. */
  const handleSortChange = useCallback(
    (value: string) => {
      const next = value || 'newest'
      setSortValue(next)
      const params = new URLSearchParams(readUrlSearchParams())
      if (next === 'newest') params.delete('sort')
      else params.set('sort', next)
      params.delete('page')
      const query = params.toString()
      const base = pathname ?? '/homes-for-sale'
      navigateQuery(router, query ? `${base}?${query}` : base, { replace: true, staticShell })
      // Immediate viewport refetch with the new sort (don't wait on full SSR),
      // over the frame the list was read for.
      searchFiltersRef.current = { ...searchFiltersRef.current, sort: next }
      void runViewportSearch(queryBoundsRef.current, drawnShapes)
    },
    [router, pathname, drawnShapes, runViewportSearch, staticShell]
  )

  const retryViewportSearch = useCallback(() => {
    void runViewportSearch(queryBoundsRef.current, drawnShapes)
  }, [drawnShapes, runViewportSearch])

  // The pins of a frame the server seeded only partly (the regional frame's
  // first card page): read the frame up to the viewport cap once the map is
  // there to draw them. Once per seed, and not when a "Show more" read already
  // brought the full set; a failed read keeps the seeded cards, which are
  // still true.
  const seedIsPartial = seedCap < VIEWPORT_ROW_CAP && initialListings.length < initialTotalCount
  useEffect(() => {
    if (!mapWanted || !seedIsPartial || initialDegraded) return
    if (pinReadGenRef.current === seedGen) return
    pinReadGenRef.current = seedGen
    if (rowsCap >= VIEWPORT_ROW_CAP) {
      setSeedReplaced(true)
      return
    }
    void runViewportSearch(queryBoundsRef.current, drawnShapes)
    // drawnShapes and rowsCap are read for the call, not triggers (a shape
    // change runs its own search); one read per seed generation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapWanted, seedGen, seedIsPartial, initialDegraded, runViewportSearch])

  // "Show more homes" past the rows in hand: read the rest of the same frame
  // (up to the viewport cap), then reveal the next page of cards.
  const readMoreRows = useCallback(async () => {
    await runViewportSearch(queryBoundsRef.current, drawnShapes)
    setVisibleCount((c) => c + CARD_PAGE)
  }, [CARD_PAGE, drawnShapes, runViewportSearch])

  // Place SELECT changed (URL city/neighborhood/subdivision) — restore the
  // geo pin and let SearchMapClustered fit the new boundary. Without this,
  // a prior pan left scopeDropped=true and the map never re-scoped.
  const placeScopeKey = `${filters.city ?? ''}|${filters.neighborhood ?? ''}|${filters.subdivision ?? ''}|${filters.postalCode ?? ''}`
  const placeScopeKeyRef = useRef(placeScopeKey)
  useEffect(() => {
    if (placeScopeKeyRef.current === placeScopeKey) return
    placeScopeKeyRef.current = placeScopeKey
    if (!placeScopeKey.replace(/\|/g, '')) return
    scopeDroppedRef.current = false
    setScopeDropped(false)
    firstBoundsReportRef.current = true
    initialSettleUntilRef.current = Date.now() + INITIAL_SETTLE_GRACE_MS
  }, [placeScopeKey])

  /** Drop the place pin (chip tap or first user map move) — see geo-scope.ts. */
  const dropGeoScope = useCallback(() => {
    if (scopeDroppedRef.current) return
    scopeDroppedRef.current = true
    setScopeDropped(true)
  }, [])

  const clearGeoScope = useCallback(() => {
    dropGeoScope()
    runViewportSearch(lastBoundsRef.current, drawnShapes)
  }, [dropGeoScope, drawnShapes, runViewportSearch])

  const handleBoundsChanged = useCallback(
    (bounds: MapBounds) => {
      lastBoundsRef.current = bounds
      // The first report is the map's initial settle, not a user gesture.
      // Every later report means the user moved the map — from then on the
      // query is the viewport itself, so the geo pin comes off.
      const isInitialSettle =
        // Event-time read: runs only when the map fires bounds-changed, never
        // during render, so SSR/client HTML cannot diverge.
        firstBoundsReportRef.current || Date.now() < initialSettleUntilRef.current // hydration-safe
      firstBoundsReportRef.current = false
      // The regional frame's settle is its camera, not a search: no ?bbox=
      // (the indexable URL stays bare) and no refetch, because the population
      // is the region, not whatever the fitted viewport reaches. A real pan
      // ends the regional frame and falls through to the map-frame path.
      if (regionFrameRef.current) {
        if (isInitialSettle) return
        setRegion(false)
      }
      // Event-time URL — closed-over urlSearchParams races the filter dock's
      // pushState and was wiping minPrice after a V3Range commit (SITE-72).
      const cameraUrl = nextSearchUrlWithBbox(
        pathname ?? '/homes-for-sale',
        readUrlSearchParams(),
        bounds,
      )
      if (cameraUrl) navigateQuery(router, cameraUrl, { replace: true, staticShell })
      if (isInitialSettle === false && !lockPlace) dropGeoScope()
      if (isInitialSettle) return
      // Camera only. List + pins stay until Search this area.
      setAreaDirty(true)
    },
    [dropGeoScope, pathname, router, lockPlace, staticShell, setRegion]
  )

  /** Reflect the drawn shape set into the URL so reload/share reproduce it.
   *  `?shapes=` carries the full multi-shape set; a plain single freeform
   *  include polygon ALSO mirrors into legacy `?poly=` (same encoding as the
   *  SEO route) so every existing ?poly= consumer keeps working. Clearing
   *  removes both params. */
  const syncShapesToUrl = useCallback(
    (shapes: DrawnShape[]) => {
      const params = new URLSearchParams(readUrlSearchParams())
      const encoded = encodeMapShapes(shapes)
      if (encoded) params.set('shapes', encoded)
      else params.delete('shapes')
      const single =
        shapes.length === 1 && shapes[0].type === 'polygon' && !shapes[0].exclude ? shapes[0] : null
      const polyEncoded = single ? encodeMapPolygon(single.points) : undefined
      if (polyEncoded) params.set('poly', polyEncoded)
      else params.delete('poly')
      params.delete('page')
      const query = params.toString()
      const base = pathname ?? '/homes-for-sale'
      navigateQuery(router, query ? `${base}?${query}` : base, { replace: true, staticShell })
    },
    [router, pathname, staticShell]
  )

  // Instrumentation guard: fire search_map_draw only when a shape was ADDED
  // (not on exclude toggles / removals), with the polygon's vertex count.
  const prevShapeCountRef = useRef(initialDrawn.length)

  const handleShapesChange = useCallback(
    (shapes: DrawnShape[]) => {
      // A user-drawn include area is an explicit spatial choice — it
      // supersedes the URL's place pin the same way a pan does.
      const poly = shapes.some((s) => !s.exclude) ? shapes : null
      if (poly) dropGeoScope()
      // A drawn area is the reader's geography: the regional frame ends.
      setRegion(false)
      setDrawnShapes(shapes)
      syncShapesToUrl(shapes)
      if (shapes.length > prevShapeCountRef.current) {
        const latest = shapes[shapes.length - 1]
        if (latest.type === 'polygon') {
          fireSearchEvent('search_map_draw', buildMapDrawPayload(latest.points.length))
        } else if (latest.type === 'circle') {
          fireSearchEvent('search_map_draw', buildMapDrawPayload(0, 'circle', latest.radiusM))
        }
      }
      prevShapeCountRef.current = shapes.length
      runViewportSearch(lastBoundsRef.current, shapes)
    },
    [runViewportSearch, dropGeoScope, syncShapesToUrl, setRegion]
  )

  /**
   * Apply (or clear) a SAVED named area from the in-map picker.
   *
   * Same end state as a hand-drawn set — shapes replaced, geo scope dropped,
   * `?shapes=` rewritten, viewport refetched — with one difference: it does
   * NOT fire search_map_draw. Picking a saved area is not a draw, and counting
   * it as one would inflate the draw-adoption metric this instrumentation
   * exists to measure. prevShapeCountRef still advances so the NEXT real draw
   * compares against the right baseline.
   */
  const handleAreaShapes = useCallback(
    (shapes: DrawnShape[]) => {
      if (shapes.some((s) => !s.exclude)) dropGeoScope()
      setRegion(false)
      setDrawnShapes(shapes)
      syncShapesToUrl(shapes)
      prevShapeCountRef.current = shapes.length
      runViewportSearch(lastBoundsRef.current, shapes)
    },
    [runViewportSearch, dropGeoScope, syncShapesToUrl, setRegion]
  )

  const searchThisArea = useCallback(() => {
    setAreaDirty(false)
    runViewportSearch(lastBoundsRef.current, drawnShapes, { limit: 250 })
  }, [drawnShapes, runViewportSearch])

  const onListHover = useCallback((key: string | null) => setHoveredKey(key), [])
  const scrollListToKey = useCallback((key: string) => {
    const el = listContainerRef.current?.querySelector(`[data-listing-key="${key}"]`)
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [])
  const onMarkerHover = useCallback(
    (key: string | null) => {
      setHoveredKey(key)
      if (key) scrollListToKey(key)
    },
    [scrollListToKey],
  )
  const onMarkerClick = useCallback(
    (key: string) => {
      setSelectedKey((prev) => (prev === key ? null : key))
      setHoveredKey(key)
      scrollListToKey(key)
    },
    [scrollListToKey],
  )

  // Load the signed-in user's hidden keys once; fail open (worst case a hidden
  // home briefly reappears). Membership matches ListingKey OR MLS ListNumber.
  useEffect(() => {
    let cancelled = false
    getHiddenListingKeys()
      .catch((): string[] => [])
      .then(withDeviceHiddenKeys) // signed-out hides live on the device (W12)
      .then((keys) => { if (!cancelled && keys.length > 0) setHiddenKeys(buildHiddenKeySet(keys)) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  const onHiddenChange = useCallback((key: string, hidden: boolean) => {
    setHiddenKeys((prev) => {
      const next = new Set(prev)
      if (hidden) next.add(key)
      else next.delete(key)
      return next
    })
  }, [])

  // The hidden subtraction feeds BOTH renders: the card list slices from it and
  // the map pins derive from it — a hidden home leaves the list and the map in
  // lockstep. totalCount stays the shared server number (§0): per-user hiding
  // must not restate the area count, exactly as SearchResults keeps `total`.
  const visibleListings = useMemo(
    () => excludeHiddenListings(listings, hiddenKeys),
    [listings, hiddenKeys],
  )
  const mapListings = useMemo(() => visibleListings.map(toMapListing), [visibleListings])

  // Public Look 2026-10-22: the header printed the seed's partial total, then
  // the pin read's, and the $/sq ft basis N moved with sort and page size.
  // Counts wait for the read the page settles on; the band (and its N) is
  // built only from a settled read holding the whole frame.
  const countPending = frameCountPending({
    hydrated,
    seedIsPartial,
    seedDegraded: initialDegraded,
    mapWanted,
    pinReadDone: seedReplaced,
  })
  const wholeFrame = rowsHoldWholeFrame({ rowsInHand: listings.length, totalCount, countIsExact })
  /**
   * The claim this rail is making about the current frame, and the band the
   * cards measure themselves against. SITE-44.
   *
   * Section 0: BOTH are derived from `visibleListings` — the exact array the
   * cards render and `mapListings` pins — so the sentence, the pins and the
   * card marks cannot disagree. There is no second query and no market-wide
   * constant here; pan the map and the population changes, so the claim does.
   *
   * And both run every row through the publisher that matches THIS claim.
   * Reading ListPrice raw put "from $8K to $8.8M in Bend" over the rail, which
   * is a sentence about what a home in Bend costs — and the $8K was a
   * fractional week at Inn of the 7th Mountain (18575 Century Drive prints
   * $3,000 for a 1 bd / 662 sqft share) sitting next to commercial lease rates
   * on PropertyType 'G'. `publishWholePropertyAmount` is the rule that already
   * knows both: it is documented as the input for exactly this figure, "the
   * 'homes near this price' band, the comparison against a place median", and
   * it withholds a lease rate and a share. The CARD still prints $3,000 for
   * that share, correctly, because it prints the share label beside it; the
   * header is making a different claim and takes the stricter number.
   * `publishListingSharePricePerSqft` does the same job for the band.
   * `askCount` is the population the range describes, which is what the trace
   * names whenever it is not the whole set.
   */
  const viewClaim = useMemo(() => {
    const asks: number[] = []
    const ppsf: (number | null)[] = []
    for (const l of visibleListings) {
      const ask = publishWholePropertyAmount({
        price: l.ListPrice,
        propertyType: l.PropertyType,
        propertySubType: l.PropertySubType,
        subdivisionName: l.SubdivisionName,
        city: l.City,
        listNumber: l.ListNumber != null ? String(l.ListNumber) : null,
      })
      if (ask != null) asks.push(ask)
      ppsf.push(
        publishListingSharePricePerSqft({
          propertyType: l.PropertyType,
          propertySubType: l.PropertySubType,
          subdivisionName: l.SubdivisionName,
          city: l.City,
          listNumber: l.ListNumber != null ? String(l.ListNumber) : null,
          pricePerSqft: cardPricePerSqft(l),
        }),
      )
    }
    return {
      count: visibleListings.length,
      askCount: asks.length,
      low: asks.length > 0 ? Math.min(...asks) : null,
      high: asks.length > 0 ? Math.max(...asks) : null,
      band: settledFrameBand(ppsf, { pending: countPending, wholeFrame }),
    }
  }, [visibleListings, countPending, wholeFrame])

  /** The most specific place the filters name, for the claim's last clause. */
  const claimPlace = scopeLabel?.split(' · ')[0]?.trim() || null

  const publishedCounts = publishSearchCountPair({
    matchCount,
    viewportCount: totalCount,
    viewportCapped: capped,
  })
  // W1: every count this view prints (the claim, the sheet title) is the
  // search index's, and a chosen property type narrows its basis.
  const countTypeFiltered = searchTypeFiltered(filters)
  const filtersSummary = useMemo(() => buildFiltersSummary(filters), [filters])
  /**
   * W23 (site audit 2026-10-21, 2.2): the list's total for these filters, so a
   * place map that holds fewer homes than the list says so ("134 of 269 on the
   * map"). Only while the place pin still applies (no pan, no drawn area), on
   * a status countSearchListings reads the same way the frame does (Active,
   * Pending), and once both reads are settled, so a mid-update pair never
   * prints as a share.
   */
  const claimListTotal =
    Boolean(scopeLabel) &&
    !scopeDropped &&
    !hasDrawnShapes &&
    !soldScope &&
    (filters.status === 'Active' || filters.status === 'Pending') &&
    matchCountReady &&
    !loading
      ? matchCount
      : null
  /**
   * The claim line (lib/search/publish-split-claim.ts): the frame's exact count
   * when the rows in hand fall short of it (the display cap, or the regional
   * frame's first card page before its pins are read), with the order those
   * rows follow; otherwise the drawn count and its ask range, as before.
   */
  const claim = useMemo(
    () =>
      countPending ? null : publishSplitClaim({
        visibleCount: viewClaim.count,
        rowsInHand: listings.length,
        totalCount,
        sort: sortValue,
        sold: soldScope,
        // W1: a chosen property type is the count's basis, not every type.
        typeFiltered: countTypeFiltered,
        // The count did not come back in time: a floor, printed "N+".
        countIsFloor: !countIsExact,
        frameLabel: rowsAreRegion ? regionFrameLabel : null,
        mapMounted: mapWanted && !listOnly,
        low: viewClaim.low,
        high: viewClaim.high,
        askCount: viewClaim.askCount,
        bandCount: viewClaim.band?.n ?? null,
        listTotal: claimListTotal,
      }),
    [
      countPending,
      viewClaim,
      listings.length,
      totalCount,
      countIsExact,
      sortValue,
      soldScope,
      countTypeFiltered,
      rowsAreRegion,
      regionFrameLabel,
      mapWanted,
      listOnly,
      claimListTotal,
    ],
  )
  // The /homes-for-sale/bend intro: its total is the claim's own figure, and
  // its detached count reads the same population (lib/search/bend-detached-intro.ts).
  const bendIntro = useMemo(
    () =>
      publishBendDetachedIntro({
        claim,
        rows: visibleListings,
        frameTotal: totalCount,
        frameDetached: detachedCount,
        bendPinned:
          scopeLabel === BEND_INTRO_CITY && !scopeDropped && !hasDrawnShapes && !rowsAreRegion,
        typeFiltered: countTypeFiltered,
        sold: soldScope,
      }),
    [claim, visibleListings, totalCount, detachedCount, scopeLabel, scopeDropped, hasDrawnShapes, rowsAreRegion, countTypeFiltered, soldScope],
  )
  /**
   * The crumb row under the claim, minus whatever the claim sentence already
   * said. A lone "BEND" in caps under "…in Bend." is the sentence twice, and
   * the second one is the shouty one. The claim's cap note rides here.
   */
  const crumbs = useMemo(() => {
    const parts = filtersSummary.split(' · ').filter((p) => p && p !== claimPlace)
    if (claim?.note) parts.push(claim.note)
    if (claim?.shareNote) parts.push(claim.shareNote)
    return parts.join(' · ')
  }, [filtersSummary, claimPlace, claim])
  // More of this frame exists than is in hand, and the rows in hand were read
  // below the viewport cap (the seed's first page, or a pan's lighter read).
  const canReadMore = !resultsDegraded && rowsCap < VIEWPORT_ROW_CAP && listings.length < totalCount
  // Cos/Matt 2026-09-06 residual: kill UNSCOPED total-inventory chrome
  // (sheet peek "3,231+ homes for sale"; list "3,341 homes found"). Place-
  // scoped sheet counts stay ("26 homes for sale"). Map-viewport phrases
  // ("N homes in this map view") stay when place-scoped or after the user
  // moves the map (areaDirty).
  const placeScoped = Boolean(scopeLabel)
  const showViewportCount = placeScoped || areaDirty
  const listCountPhrase =
    countPending || !matchCountReady && totalCount === 0
      ? 'Updating…'
      : showViewportCount && publishedCounts.viewport?.phrase
        ? publishedCounts.viewport.phrase
        : loading
          ? 'Updating…'
          : 'Homes'
  // Zillow sheet title: numeric "N homes for sale" only when Places is on.
  const sheetHomesLabel = (() => {
    if (resultsDegraded) return 'Search delayed'
    if (countPending || (!matchCountReady && totalCount === 0)) return 'Updating…'
    if (!placeScoped) return 'Homes for sale'
    const pub = publishedCounts.viewport ?? publishedCounts.match
    const value = pub?.value ?? totalCount
    const plus = pub?.phrase.includes('+') ? '+' : ''
    if (value === 1 && !plus) return '1 home for sale'
    return `${formatCount(value)}${plus} homes for sale`
  })()
  // W1: the numeric sheet title names its basis beside it. The Sold scope's
  // count is closed sales, which no for-sale basis describes.
  const sheetHomesCount = (() => {
    if (resultsDegraded || soldScope || !placeScoped) return null
    if (countPending || (!matchCountReady && totalCount === 0)) return null
    const pub = publishedCounts.viewport ?? publishedCounts.match
    return searchListingCount(pub?.value ?? totalCount, { typeFiltered: countTypeFiltered })
  })()
  const sheetExpanded = mobileView === 'list'

  // listPanel is mounted once (desktop rail + mobile bottom sheet share the
  // node via CSS). Mobile: peek sheet always; List expands it; Map collapses to peek.
  // totalCount is sticky across pan refetches — loading never clears it, so
  // the row / sheet show previous N + "Updating…" (SEARCH_UX_WAVE3 pan sticky count).
  const listPanel = (
    <div ref={listContainerRef} className="map-search-list__scroll flex-1 min-h-0 overflow-y-auto bg-muted">
      {/* The claim (SITE-44). This rail used to open with a bare count and a
          crumb string — the taste table's "no claim tying list and map into one
          object". It now states, in a sentence, what the frame is showing: how
          many homes are drawn, the ask range across them, and the place, with
          the section 0 trace under it. Sort still lives on the floating
          Map|Sort pill (Matt 2026-09-07). */}
      <header className="srch-claim sticky top-0 z-10 px-4 py-2.5 sm:py-3" aria-live="polite">
        {resultsDegraded ? (
          <p className="srch-claim__line">
            <span className="font-semibold">Search delayed.</span> We could not read this view in
            time. That is a connection problem, not an empty market.
          </p>
        ) : (
          <>
            <p className="srch-claim__line">
              {claim ? (
                <>
                  <span className="srch-figure">
                    {formatCount(claim.figure)}
                    {claim.figureIsFloor ? '+' : ''}
                  </span>
                  <span className="srch-claim__rest">
                    {` ${claim.noun}${claim.where}`}
                    {/* W1: the basis beside the figure, so it reads apart from
                        the detached single-family figures below the map. */}
                    {claim.basis ? <span className="srch-claim__basis">{`, ${claim.basis.label}`}</span> : null}
                    {claim.range ? (
                      claim.range.low === claim.range.high ? (
                        <>
                          {', asking '}
                          <span className="srch-figure srch-figure--inline">{formatPriceCompact(claim.range.low)}</span>
                        </>
                      ) : (
                        <>
                          {', '}
                          <span className="srch-figure srch-figure--inline">{formatPriceCompact(claim.range.low)}</span>
                          {'–'}
                          <span className="srch-figure srch-figure--inline">{formatPriceCompact(claim.range.high)}</span>
                        </>
                      )
                    ) : null}
                    {claim.order ? `, ${claim.order}` : ''}
                    {claimPlace ? ` · ${claimPlace}` : ''}
                  </span>
                </>
              ) : (
                <>{listCountPhrase === 'Updating…' ? 'Reading this view…' : 'No homes are drawn on this map yet.'}</>
              )}
            </p>
            {bendIntro ? <p className="srch-claim__intro">{bendIntro.text}</p> : null}
            {crumbs || loading ? (
              <p className="srch-claim__crumbs">
                {crumbs}
                {crumbs && loading ? ' · ' : ''}
                {loading ? 'Updating…' : ''}
              </p>
            ) : null}
            {claim ? (
              // The trace is present on the claim, but collapsed: four lines
              // of methodology under a one-line sentence is the wall of text
              // TASTE.md bans, and this header sits above every scroll of the
              // rail. One word, "Source", and the whole trace on a click.
              <V3SourceDisclosure className="srch-claim__source" source={claim.source} />
            ) : null}
          </>
        )}
      </header>
      {resultsDegraded ? (
        <SearchEmpty
          kind="degraded"
          title="Search took too long"
          description="We could not load homes for this view. Try again, or reload the page."
          action={{
            label: loading ? 'Trying…' : 'Try again',
            onClick: retryViewportSearch,
          }}
        />
      ) : !matchCountReady && listings.length === 0 ? (
        <SearchEmpty
          kind="updating"
          title="Checking homes for this view"
          description="The filter-match count is still loading. This is not an empty market."
        />
      ) : listings.length === 0 ? (
        hasNarrowingFilters ? (
          <SearchEmpty
            kind={beyondViewportCount != null ? 'outside' : 'empty'}
            title={
              beyondViewportCount != null
                ? `${beyondViewportCount.toLocaleString('en-US')} matching home${beyondViewportCount === 1 ? ' is' : 's are'} outside this map view`
                : 'No homes match these filters here'
            }
            description={
              beyondViewportCount != null
                ? 'Zoom out to see them, or loosen a filter.'
                : 'Loosen a filter, or zoom out to widen the search area.'
            }
            action={{
              label: 'Clear all filters',
              onClick: () =>
                navigateQuery(router, `${pathname ?? '/homes-for-sale'}?view=${filters.view ?? 'split'}`, {
                  staticShell,
                }),
            }}
          />
        ) : (
          <SearchEmpty
            kind="empty"
            title="No homes in this part of the map"
            description="Zoom out or pan to a different area to see listings."
          />
        )
      ) : (
        <>
        <div className="v3-lrow-list px-4 py-2">
          {visibleListings.slice(0, visibleCount).map((l, cardIndex) => {
            const key = rowKey(l)
            const href = listingTileHref({
              listingKey: key,
              listNumber: l.ListNumber ?? null,
              streetNumber: l.StreetNumber,
              streetName: l.StreetName,
              city: l.City,
              boundaryCity: l.BoundaryCity ?? null,
              boundaryNeighborhood: l.BoundaryNeighborhood ?? null,
              subdivisionName: l.SubdivisionName,
            })
            const isHovered = hoveredKey === key
            const isSelected = selectedKey === key
            const addressLine = cardStreet(l)
            const badges = nowMs != null ? cardBadges(l, nowMs, openHouseLabels) : []
            // V3ListingRow — the Ledger-register listing unit (same as
            // SearchResults). Wrapper owns data-listing-key + hide control;
            // the map hover/select state rides the row's own functional
            // inset ring (`is-hot` / `is-active`), never an elevation shadow.
            return (
              <div
                key={key}
                data-listing-key={key}
                className="group group/hide relative"
                onMouseEnter={() => onListHover(key)}
                onMouseLeave={() => onListHover(null)}
                onClick={() => setSelectedKey(key)}
              >
                <ListingCardHideControl
                  listingKey={key}
                  addressLine={addressLine}
                  onVisibilityChange={onHiddenChange}
                  className="left-auto right-1 top-1 size-7"
                />
                <SplitListingCard
                  listingKey={l.ListingKey ?? null}
                  href={href}
                  photoUrls={
                    l.photoUrls && l.photoUrls.length > 0
                      ? l.photoUrls.map((url) => listingRowPhotoSrc(url))
                      : l.PhotoURL
                        ? [listingRowPhotoSrc(l.PhotoURL)]
                        : []
                  }
                  price={l.ListPrice}
                  addressLine={addressLine}
                  cityLine={cardCity(l)}
                  beds={l.BedroomsTotal}
                  baths={l.BathroomsTotal}
                  sqft={rowSqft(l)}
                  pricePerSqft={cardPricePerSqft(l)}
                  propertyType={l.PropertyType ?? null}
                  propertySubType={l.PropertySubType ?? null}
                  subdivisionName={l.SubdivisionName ?? null}
                  city={l.City ?? null}
                  listNumber={l.ListNumber ?? null}
                  listOfficeName={l.ListOfficeName}
                  badges={badges}
                  hasTour={l.has_virtual_tour === true || Boolean(l.tourUrl)}
                  tourUrl={l.tourUrl ?? null}
                  // No tourLabel: the card reads it off tourUrl in the listing
                  // page's words (publishListingTourLabel), as every dial does.
                  ppsfBand={viewClaim.band}
                  priority={cardIndex === 0}
                  className={cn(isSelected && 'is-active', !isSelected && isHovered && 'is-hot')}
                  onOpenTour={
                    l.tourUrl || l.has_virtual_tour
                      ? () => {
                          const embed = publishTourEmbedFromUrl(l.tourUrl, l.PhotoURL)
                          if (embed) setTour(embed)
                          else window.location.assign(`${href}#tour`)
                        }
                      : undefined
                  }
                />
              </div>
            )
          })}
        </div>
        {(visibleListings.length > visibleCount || canReadMore) && (
          <SearchPagerMore
            label={loading ? 'Loading more homes…' : 'Show more homes'}
            onClick={() => {
              if (loading) return
              if (visibleListings.length > visibleCount) setVisibleCount((c) => c + CARD_PAGE)
              else void readMoreRows()
            }}
          />
        )}
        {/* ODS Aug 2024 IDX display rules (G54): every rendered price/$-per-sqft
            needs source attribution in view. Split/map is an app frame that
            deliberately omits V3Footer, so it carries no attribution of its
            own — this line mirrors V3Footer's ODS text verbatim (compact,
            no broker-license line, since this is not the global chrome). */}
        <p className="px-4 pb-4 text-xs text-muted-foreground">
          Listing data comes from Oregon Data Share and Morgan Data Shuttle. Information deemed
          reliable but not guaranteed.
        </p>
        </>
      )}
    </div>
  )

  /** Sort button + its menu. Each floating pill owns its own open menu. */
  const renderSortControl = (where: 'map' | 'door') => (
    <div className="relative">
      <button
        type="button"
        className="map-search-mapsort__sort"
        aria-expanded={sortOpen === where}
        aria-haspopup="listbox"
        aria-label={`Sort: ${sortLabel}`}
        onClick={() => setSortOpen((o) => (o === where ? null : where))}
      >
        Sort
      </button>
      {sortOpen === where ? (
        <ul
          className="srch-pop map-search-mapsort__menu absolute bottom-full left-1/2 z-[120] mb-2 min-w-[14rem] -translate-x-1/2 py-1"
          role="listbox"
          aria-label="Sort results"
        >
          {sortOptions.map((o) => (
            <li key={o.value}>
              <button
                type="button"
                role="option"
                aria-selected={sortValue === o.value}
                className={cn(
                  'flex w-full items-center px-3 py-2 text-left text-sm',
                  sortValue === o.value ? 'bg-muted font-medium text-foreground' : 'text-foreground',
                )}
                onClick={() => {
                  handleSortChange(o.value)
                  setSortOpen(null)
                }}
              >
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )

  const mapPanel = listOnly ? null : (
    <div className="relative h-full min-h-0 min-w-0 flex-1">
      {mapWanted ? (
      <SearchMapClustered
        listings={mapListings}
        savedListingKeys={viewerState.savedListingKeys}
        likedListingKeys={viewerState.likedListingKeys}
        placeQuery={placeQuery}
        boundaryGeojson={boundaryGeojson}
        overlayBoundaries={overlayBoundaries}
        hideBoundaryToggle={lockPlace}
        initialBounds={initialBounds}
        lockBounds
        // The regional frame's box fills the pane (fractional fit) instead of
        // opening at integer z8; a place's own boundary camera is unchanged.
        fractionalZoom={Boolean(regionFrameLabel)}
        relayoutKey={mobileView}
        onBoundsChanged={handleBoundsChanged}
        shapes={drawnShapes}
        onShapesChange={handleShapesChange}
        hoveredKey={hoveredKey ?? selectedKey}
        onMarkerHover={onMarkerHover}
        onMarkerClick={onMarkerClick}
        className="srch-map-field h-full w-full"
      />
      ) : (
        <MapLoading />
      )}
      {/* Saved named areas (Flexmls My-Map-Overlays parity). Applying one
          replaces the drawn shape set, so it rides the identical ?shapes=
          contract and is shareable + alert-savable like any drawn area. */}
      <AreaPicker shapes={drawnShapes} onApply={handleAreaShapes} />
      {/* Zillow-style floating Map | Sort pill (Matt 2026-09-07). */}
      <div className="map-search-mapsort pointer-events-none absolute bottom-[5.5rem] left-1/2 z-[110] -translate-x-1/2 lg:bottom-6">
        <div className="map-search-mapsort__pill pointer-events-auto" role="group" aria-label="Map and sort">
          <div className="map-search-views map-search-mapsort__views" role="radiogroup" aria-label="View">
            <button
              type="button"
              role="radio"
              aria-checked={layoutView === 'map'}
              aria-label="Map view"
              onClick={() => applyView('map')}
            >
              Map
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={layoutView === 'split'}
              aria-label="Split view"
              className="max-lg:hidden"
              onClick={() => applyView('split')}
            >
              Split
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={layoutView === 'list'}
              aria-label="List view"
              onClick={() => applyView('list')}
            >
              List
            </button>
          </div>
          <span className="map-search-mapsort__rule" aria-hidden />
          {renderSortControl('map')}
        </div>
      </div>
      {areaDirty ? (
        <Button
          type="button"
          variant="outline"
          onClick={searchThisArea}
          className="absolute left-1/2 top-3 z-[100] h-11 -translate-x-1/2 rounded-none border-border bg-card px-4 text-sm font-medium text-foreground shadow-none"
        >
          Search this area
        </Button>
      ) : null}
      {/* Active place scope — visible until the first user map move, then the
          query is pure bounding-box. Tap the chip to drop the scope now. */}
      {scopeLabel && scopeDropped === false && !lockPlace ? (
        <Button
          type="button"
          variant="outline"
          onClick={clearGeoScope}
          aria-label={`Showing ${scopeLabel} only. Clear to search the whole map area.`}
          className="absolute left-1/2 top-[3.6rem] z-[100] flex h-auto -translate-x-1/2 items-center gap-2 rounded-none border-border bg-card px-3.5 py-1.5 text-xs font-medium text-foreground shadow-none transition hover:bg-muted"
        >
          <span>
            Showing <span className="font-semibold">{scopeLabel}</span> only
          </span>
          <span
            aria-hidden
            className="flex h-4 w-4 items-center justify-center rounded-none bg-foreground/10 text-xs leading-none"
          >
            ✕
          </span>
        </Button>
      ) : null}
      {/* Canvas-level fetch state — the map itself says when results are stale. */}
      {loading ? (
        <div className="pointer-events-none absolute inset-0 z-[95] bg-background/20" aria-hidden />
      ) : null}
      <div
        role="status"
        aria-live="polite"
        className={cn(
          // Sit above the mobile count pill / sheet so the spinner stays readable.
          'pointer-events-none absolute bottom-[4.75rem] left-1/2 z-[100] -translate-x-1/2 transition-opacity lg:bottom-16',
          loading ? 'opacity-100' : 'opacity-0'
        )}
      >
        <span className="flex items-center gap-2 rounded-none border border-border bg-card px-3.5 py-1.5 text-xs font-medium text-foreground shadow-none">
          <span
            aria-hidden
            className="h-3 w-3 animate-spin rounded-full border-2 border-foreground/30 border-t-foreground"
          />
          {loading ? 'Updating results…' : 'Results updated'}
        </span>
      </div>
      {/* Mobile count lives on the bottom sheet chrome ("N homes for sale"). */}
    </div>
  )

  return (
    <div className="map-search-shell srch-atlas flex min-h-0 flex-1 w-full flex-col overflow-hidden" style={{ contain: 'layout' }}>
      {/* List + map, ONE mount each. Desktop: side-by-side. Mobile: full-bleed
          map under a Zillow-style bottom sheet (peek → expand). Never double-render. */}
      <div className="relative flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* Map — full-bleed under the list on mobile; flex-1 rail partner on desktop. */}
        {listOnly ? null : (
        <div
          className={cn(
            'absolute inset-0 z-0 min-h-0 min-w-0 lg:static lg:relative lg:z-auto lg:order-2 lg:min-h-0 lg:flex-1',
            layoutView === 'list' ? 'lg:hidden' : null,
          )}
        >
          {mapPanel}
        </div>
        )}

        {/* List rail / mobile bottom sheet.
            Mobile: peek "N homes for sale" always; List expands; Map collapses.
            Desktop: left rail (hidden in pure map). */}
        <div
          className={cn(
            'z-10 min-h-0 flex-col bg-card',
            listOnly
              ? cn(
                  /* SITE-59: `absolute inset-0` here was the map's doing — the
                     rail floated over a full-bleed canvas on phones. `listOnly`
                     has had no canvas under it since 2026-09-03, and an
                     absolutely positioned rail contributes no height, so the
                     section it sits in could only be held open by the fixed
                     map height that this node removed. In flow it is as tall
                     as the list, and `w-full` because there is no map beside
                     it to share the row with at any width. */
                  'relative flex w-full',
                  'map-search-list lg:static lg:order-1 lg:z-auto lg:h-auto lg:max-h-none lg:shrink-0 lg:rounded-none lg:border-t-0 lg:border-r lg:border-border lg:shadow-none lg:transition-none lg:inset-auto',
                  layoutView === 'list' ? 'lg:w-full lg:flex-1' : null,
                )
              : cn(
                  'map-search-list map-search-sheet',
                  sheetExpanded ? 'is-expanded' : 'is-peek',
                  // Phones opening on the list: the expanded sheet IS the page
                  // below the filter dock (search-ledger.css).
                  listFirst && 'map-search-sheet--list-first',
                  'lg:static lg:order-1 lg:z-auto lg:flex lg:h-auto lg:max-h-none lg:shrink-0 lg:rounded-none lg:border-t-0 lg:border-r lg:border-border lg:shadow-none lg:transition-none lg:inset-auto',
                  layoutView === 'map' ? 'lg:hidden' : 'lg:flex',
                  layoutView === 'list' ? 'lg:w-full lg:flex-1' : null,
                ),
          )}
        >
          {listOnly ? null : (
            <button
              type="button"
              className="map-search-sheet__chrome"
              aria-expanded={sheetExpanded}
              aria-controls="map-search-sheet-panel"
              aria-label={sheetExpanded ? 'Collapse home list' : 'Expand home list'}
              onPointerDown={(e) => {
                sheetDragRef.current = { startY: e.clientY, expanded: sheetExpanded }
                e.currentTarget.setPointerCapture?.(e.pointerId)
              }}
              onPointerUp={(e) => {
                const drag = sheetDragRef.current
                sheetDragRef.current = null
                if (!drag) return
                const dy = drag.startY - e.clientY
                if (dy > 40 && !drag.expanded) {
                  applyView('list')
                  return
                }
                if (dy < -40 && drag.expanded) {
                  applyView('map')
                  return
                }
                if (Math.abs(dy) < 10) {
                  applyView(drag.expanded ? 'map' : 'list')
                }
              }}
            >
              <span className="map-search-sheet__handle" aria-hidden />
              {/* SITE-44 / SITE-72: peek names two populations — filter match
                  vs pins on this frame. Ask range stays inside the sheet body
                  (same claim as desktop) so a 375 camera that frames a
                  different pin set cannot print a second top-of-range on the
                  fold and look like the page disagrees with itself. */}
              <span className="srch-count map-search-sheet__title" aria-live="polite">
                <span className="srch-figure font-semibold">{sheetHomesLabel}</span>
                {sheetHomesCount ? (
                  <span className="map-search-sheet__basis">{`, ${sheetHomesCount.basis.label}`}</span>
                ) : null}
                {viewClaim.count > 0 && !resultsDegraded && !countPending ? (
                  <span className="map-search-sheet__range">
                    {/* W23: the claim's own words for what is on the map
                        (its cap note, its list share), so the peek never
                        prints a second on-map total, including the seed page
                        before the pins are read. */}
                    {`· ${[claim?.note, claim?.shareNote].filter(Boolean).join(' · ') || `${formatCount(viewClaim.count)} on this map`}`}
                    {/* A range over the first rows of a larger frame is not the
                        frame's range, so the peek prints one only when every
                        row is in hand (publishSplitClaim). */}
                    {claim?.range
                      ? claim.range.low === claim.range.high
                        ? ` · ${formatPriceCompact(claim.range.low)}`
                        : ` · ${formatPriceCompact(claim.range.low)}–${formatPriceCompact(claim.range.high)}`
                      : ''}
                  </span>
                ) : null}
                {loading ? <span className="ml-1.5 text-xs text-muted-foreground">Updating…</span> : null}
              </span>
            </button>
          )}
          <div
            id="map-search-sheet-panel"
            className="map-search-sheet__body flex min-h-0 flex-1 flex-col"
          >
            {listPanel}
          </div>
          {/* Phones opening on the list: the map is one tap away on the same
              floating Map | Sort pill the map carries (Matt 2026-09-07). Shown
              below lg only (search-ledger.css); from lg up the map is beside
              the list and carries its own pill. */}
          {listFirst && mobileView === 'list' ? (
            <div className="map-search-mapdoor pointer-events-none">
              <div className="map-search-mapsort__pill pointer-events-auto" role="group" aria-label="Map and sort">
                <button
                  type="button"
                  className="map-search-mapdoor__map"
                  aria-label="Show the map"
                  onClick={() => applyView('map')}
                >
                  Map
                </button>
                <span className="map-search-mapsort__rule" aria-hidden />
                {renderSortControl('door')}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <ListingTourOverlay
        open={tour != null}
        video={tour}
        title="Listing tour"
        onClose={() => setTour(null)}
      />
    </div>
  )
}
